import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Filter,
  MapPin,
  MessageCircle,
  Search,
  ShieldCheck,
  Star,
  Stethoscope,
  User,
} from 'lucide-react';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { getSpecialtyCitySeoPage } from '../services/searchApi';
import { absoluteUrl, createOrganizationSchema, getSpecialtySeoImagePath } from '../utils/seo';

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }).format(new Date(value));
  } catch {
    return value;
  }
};

const splitIntro = (text = '') => text.split(/\n+/).filter(Boolean);

const DoctorSeoCard = ({ doctor, whatsappUrl }) => {
  const plans = doctor.accepted_health_plans || [];
  const profileUrl = doctor.slug ? `/${doctor.slug}` : `/medico/${doctor.id}`;
  const isVerifiedProfile = doctor.profile_trust_level === 'verificado';
  const registrationType = String(doctor.professional_registration_type || (doctor.crm ? 'crm' : '')).toUpperCase();
  const registrationNumber = doctor.professional_registration_number || doctor.crm;
  const registrationUf = doctor.professional_registration_uf || doctor.crm_state;

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-950/5">
      <div className="flex gap-4">
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-slate-100">
          {doctor.photo ? (
            <img src={doctor.photo} alt={`Foto de ${doctor.full_name}`} loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <User className="h-9 w-9 text-slate-400" />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap gap-2">
            {isVerifiedProfile && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                <ShieldCheck className="h-3.5 w-3.5" />
                Perfil verificado
              </span>
            )}
            {plans.length > 0 && (
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                Aceita convênio
              </span>
            )}
          </div>
          <h2 className="text-lg font-bold leading-tight text-slate-950">
            <Link to={profileUrl} className="hover:text-emerald-700">
              {doctor.full_name}
            </Link>
          </h2>
          <p className="mt-1 text-sm font-medium text-slate-600">{doctor.specialty_name}</p>
          {registrationNumber && (
            <p className="mt-1 text-xs text-slate-500">
              {registrationType || 'Registro'} informado {registrationNumber}{registrationUf ? `/${registrationUf}` : ''}
            </p>
          )}
        </div>
      </div>

      {(doctor.address || doctor.city) && (
        <div className="mt-4 flex gap-2 rounded-2xl bg-slate-50 p-3 text-sm text-slate-600">
          <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
          <span>{[doctor.address || doctor.provider_name, doctor.neighborhood, doctor.city, doctor.state].filter(Boolean).join(', ')}</span>
        </div>
      )}

      {plans.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {plans.slice(0, 5).map((plan) => (
            <span key={plan} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600">
              {plan}
            </span>
          ))}
          {plans.length > 5 && <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-500">+{plans.length - 5}</span>}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
        <div className="flex items-center gap-1.5 text-sm">
          {doctor.rating_avg > 0 ? (
            <>
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              <strong>{doctor.rating_avg.toFixed(1)}</strong>
              <span className="text-slate-500">({doctor.rating_count || 0} avaliações)</span>
            </>
          ) : (
            <span className="text-slate-500">Avaliações em atualização</span>
          )}
        </div>
        <div className="flex gap-2">
          {whatsappUrl && (
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-2 text-sm font-semibold text-green-700 hover:bg-green-100">
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
          )}
          <Link to={profileUrl} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-900 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-800">
            Ver perfil
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
};

const SpecialtyCityPage = () => {
  const { specialtySlug, cityUf } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [minRating, setMinRating] = useState('');
  const [symptom, setSymptom] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    getSpecialtyCitySeoPage(specialtySlug, cityUf)
      .then((data) => {
        if (!active) return;
        setPage(data);
        setError('');
      })
      .catch(() => {
        if (!active) return;
        setError('Não foi possível carregar esta página agora.');
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [specialtySlug, cityUf]);

  const filteredDoctors = useMemo(() => {
    const doctors = page?.doctors || [];
    return doctors.filter((doctor) => {
      const planOk = !selectedPlan || (doctor.accepted_health_plans || []).some((plan) => plan === selectedPlan);
      const ratingOk = !minRating || Number(doctor.rating_avg || 0) >= Number(minRating);
      return planOk && ratingOk;
    });
  }, [page, selectedPlan, minRating]);

  const structuredData = useMemo(() => {
    if (!page) return createOrganizationSchema();
    const pageUrl = absoluteUrl(page.canonical_path);
    return [
      createOrganizationSchema(),
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: page.title,
        description: page.description,
        url: pageUrl,
        isPartOf: { '@type': 'WebSite', name: 'BuscaDoutor', url: absoluteUrl('/') },
        about: { '@type': 'MedicalSpecialty', name: page.specialty.name },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Início', item: absoluteUrl('/') },
          { '@type': 'ListItem', position: 2, name: 'Especialistas', item: absoluteUrl('/buscar') },
          { '@type': 'ListItem', position: 3, name: `${page.specialty.name} em ${page.city}`, item: pageUrl },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: page.faq.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'MedicalClinic',
        name: `BuscaDoutor - ${page.specialty.name} em ${page.city}`,
        url: pageUrl,
        areaServed: `${page.city}, ${page.state}`,
        medicalSpecialty: page.specialty.name,
        address: { '@type': 'PostalAddress', addressLocality: page.city, addressRegion: page.state, addressCountry: 'BR' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: `BuscaDoutor - ${page.specialty.name} em ${page.city}`,
        url: pageUrl,
        areaServed: `${page.city}, ${page.state}`,
        address: { '@type': 'PostalAddress', addressLocality: page.city, addressRegion: page.state, addressCountry: 'BR' },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: (page.doctors || []).slice(0, 12).map((doctor, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Physician',
            name: doctor.full_name,
            url: absoluteUrl(doctor.slug ? `/${doctor.slug}` : `/medico/${doctor.id}`),
            medicalSpecialty: doctor.specialty_name || page.specialty.name,
            address: { '@type': 'PostalAddress', addressLocality: doctor.city || page.city, addressRegion: doctor.state || page.state, addressCountry: 'BR' },
          },
        })),
      },
    ];
  }, [page]);

  const handleHeroSearch = () => {
    if (!page) return;
    const params = new URLSearchParams({
      specialty_name: page.specialty.name,
      city: page.city,
      state: page.state,
    });
    if (selectedPlan) params.set('plan', selectedPlan);
    if (symptom) params.set('symptom', symptom);
    navigate(`/buscar?${params.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <main className="mx-auto max-w-6xl px-4 py-20">
          <div className="h-8 w-56 animate-pulse rounded-full bg-slate-200" />
          <div className="mt-8 h-16 max-w-3xl animate-pulse rounded-3xl bg-slate-200" />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[1, 2, 3].map((item) => <div key={item} className="h-48 animate-pulse rounded-3xl bg-white" />)}
          </div>
        </main>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SEOHead title="Especialistas | BuscaDoutor" description="Encontre médicos e especialistas perto de você." robots="noindex, follow" />
        <Navbar />
        <main className="mx-auto max-w-3xl px-4 py-24 text-center">
          <h1 className="text-3xl font-bold text-slate-950">Página não encontrada</h1>
          <p className="mt-4 text-slate-600">{error || 'Não encontramos médicos suficientes para esta combinação.'}</p>
          <Link to="/buscar" className="mt-8 inline-flex rounded-full bg-emerald-900 px-5 py-3 font-semibold text-white">Ir para a busca</Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7FAF9]">
      <SEOHead
        title={page.title}
        description={page.description}
        canonicalUrl={page.canonical_path}
        robots={page.robots}
        imageUrl={getSpecialtySeoImagePath(page.specialty.slug || page.specialty.name)}
        structuredData={structuredData}
      />
      <Navbar />

      <main>
        <section className="border-b border-slate-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-5 text-sm text-slate-500">
            <nav className="flex flex-wrap items-center gap-1" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-emerald-800">Início</Link>
              <ChevronRight className="h-4 w-4" />
              <Link to="/buscar" className="hover:text-emerald-800">Especialistas</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="font-medium text-slate-800">{page.specialty.name} em {page.city}</span>
            </nav>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 lg:grid-cols-[1.1fr_0.9fr] lg:py-20">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-800">
                <Stethoscope className="h-4 w-4" />
                Guia médico local
              </span>
              <h1 className="mt-6 max-w-4xl text-4xl font-black tracking-tight text-slate-950 md:text-6xl">
                {page.specialty.name} em {page.city}
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600">
                Encontre {page.specialty.name.toLowerCase()} em {page.city}, compare perfis, convênios, localização e escolha o especialista ideal para você.
              </p>

              <div className="mt-8 grid gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-3 shadow-sm md:grid-cols-[1fr_1fr_auto]">
                <select value={selectedPlan} onChange={(event) => setSelectedPlan(event.target.value)} className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm text-slate-700 outline-none focus:border-emerald-500">
                  <option value="">Convênio</option>
                  {page.health_plans.map((plan) => <option key={plan.name} value={plan.name}>{plan.name}</option>)}
                </select>
                <input value={symptom} onChange={(event) => setSymptom(event.target.value)} placeholder="Sintoma, ex: dor no peito" className="h-12 rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:border-emerald-500" />
                <button onClick={handleHeroSearch} className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-emerald-900 px-5 font-bold text-white hover:bg-emerald-800">
                  <Search className="h-4 w-4" />
                  Encontrar {page.specialty.name}
                </button>
              </div>
            </div>

            <aside className="rounded-[2rem] border border-emerald-100 bg-gradient-to-br from-emerald-950 to-emerald-800 p-6 text-white shadow-2xl shadow-emerald-950/15">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-100">Confiança e cobertura</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-3xl bg-white/10 p-5">
                  <strong className="text-4xl">{page.total_doctors}</strong>
                  <p className="mt-1 text-sm text-emerald-50">médicos encontrados</p>
                </div>
                <div className="rounded-3xl bg-white/10 p-5">
                  <strong className="text-4xl">{page.total_reviews}</strong>
                  <p className="mt-1 text-sm text-emerald-50">avaliações registradas</p>
                </div>
                <div className="rounded-3xl bg-white/10 p-5">
                  <strong className="text-4xl">{page.health_plans.length}</strong>
                  <p className="mt-1 text-sm text-emerald-50">convênios mapeados</p>
                </div>
                <div className="rounded-3xl bg-white/10 p-5">
                  <strong className="text-2xl">{formatDate(page.trust.last_updated)}</strong>
                  <p className="mt-1 text-sm text-emerald-50">última atualização</p>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14">
          <div className="grid gap-10 lg:grid-cols-[0.78fr_1.22fr]">
            <article className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-800">Contexto médico</p>
              <div className="mt-5 space-y-5 text-base leading-8 text-slate-600">
                {splitIntro(page.intro).map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </div>
            </article>

            <div className="grid gap-5 md:grid-cols-2">
              <article className="rounded-[2rem] border border-slate-200 bg-white p-7">
                <h2 className="text-2xl font-black text-slate-950">Quando procurar {page.specialty.name.toLowerCase()}?</h2>
                <ul className="mt-5 space-y-3">
                  {page.when_to_seek.map((item) => (
                    <li key={item} className="flex gap-3 text-slate-700">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
              <article className="rounded-[2rem] border border-slate-200 bg-white p-7">
                <h2 className="text-2xl font-black text-slate-950">Condições tratadas</h2>
                <ul className="mt-5 space-y-3">
                  {page.conditions.map((item) => (
                    <li key={item} className="flex gap-3 text-slate-700">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-14">
          <div className="mb-7 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-emerald-800">Médicos na cidade</p>
              <h2 className="mt-2 text-3xl font-black text-slate-950">Perfis de {page.specialty.name.toLowerCase()} em {page.city}</h2>
            </div>
            <div className="flex flex-wrap gap-3 rounded-3xl border border-slate-200 bg-white p-3">
              <span className="inline-flex items-center gap-2 px-2 text-sm font-semibold text-slate-600"><Filter className="h-4 w-4" /> Filtros</span>
              <select value={selectedPlan} onChange={(event) => setSelectedPlan(event.target.value)} className="h-10 rounded-2xl border border-slate-200 px-3 text-sm">
                <option value="">Todos os convênios</option>
                {page.health_plans.map((plan) => <option key={plan.name} value={plan.name}>{plan.name}</option>)}
              </select>
              <select value={minRating} onChange={(event) => setMinRating(event.target.value)} className="h-10 rounded-2xl border border-slate-200 px-3 text-sm">
                <option value="">Qualquer nota</option>
                <option value="4">Nota 4+</option>
                <option value="4.5">Nota 4.5+</option>
              </select>
            </div>
          </div>

          {filteredDoctors.length > 0 ? (
            <div className="grid gap-5 lg:grid-cols-2">
              {filteredDoctors.map((doctor) => (
                <DoctorSeoCard key={doctor.id} doctor={doctor} whatsappUrl={page.whatsapp_urls?.[doctor.id]} />
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center">
              <p className="text-lg font-semibold text-slate-900">Nenhum médico encontrado com estes filtros.</p>
              <button onClick={() => { setSelectedPlan(''); setMinRating(''); }} className="mt-4 rounded-full bg-emerald-900 px-5 py-3 font-semibold text-white">Limpar filtros</button>
            </div>
          )}
        </section>

        <section className="mx-auto grid max-w-7xl gap-6 px-4 py-14 lg:grid-cols-[1fr_0.9fr]">
          <article className="rounded-[2rem] border border-slate-200 bg-white p-8">
            <h2 className="text-3xl font-black text-slate-950">Perguntas frequentes</h2>
            <div className="mt-6 divide-y divide-slate-100">
              {page.faq.map((item) => (
                <details key={item.question} className="group py-5">
                  <summary className="cursor-pointer list-none text-lg font-bold text-slate-900 group-open:text-emerald-800">{item.question}</summary>
                  <p className="mt-3 leading-7 text-slate-600">{item.answer}</p>
                </details>
              ))}
            </div>
          </article>

          <aside className="rounded-[2rem] border border-slate-200 bg-white p-8">
            <h2 className="text-2xl font-black text-slate-950">Explore também</h2>
            {[
              ['Outras especialidades na cidade', page.internal_links.related_specialties],
              ['Convênios da especialidade', page.internal_links.health_plans],
              ['Sintomas relacionados', page.internal_links.symptoms],
              ['Médicos similares', page.internal_links.similar_doctors],
            ].map(([title, links]) => (
              links?.length > 0 && (
                <div key={title} className="mt-6">
                  <h3 className="text-sm font-bold uppercase tracking-[0.14em] text-slate-500">{title}</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {links.map((link) => (
                      <Link key={`${title}-${link.url}-${link.label}`} to={link.url} className="rounded-full border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-800">
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )
            ))}
          </aside>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-16">
          <div className="rounded-[2rem] bg-slate-950 p-8 text-white md:p-12">
            <h2 className="text-3xl font-black">Ainda procurando um {page.specialty.name.toLowerCase()} em {page.city}?</h2>
            <p className="mt-3 max-w-3xl text-slate-300">
              Use nossa busca inteligente para encontrar especialistas por sintomas, convênio e localização.
            </p>
            <button onClick={handleHeroSearch} className="mt-7 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-bold text-slate-950 hover:bg-emerald-50">
              Encontrar especialista
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SpecialtyCityPage;
